import type { MemberAmounts, ProposalResponse, TierBreakdown } from '@/types/waterfall'
import { MEMBERS, type MemberCode } from '@/types/waterfall'

export type BuildupRowKind =
  | 'cash_in'
  | 'true_up'
  | 'current_period'
  | 'cap_adjustment'
  | 'subtotal'
  | 'remaining'

export interface BuildupRow {
  kind: BuildupRowKind
  label: string
  amounts: MemberAmounts
  total: number
  indent: number
}

const ZERO: MemberAmounts = { JR: 0, KB: 0, LVW: 0, TR: 0, SM: 0 }

function memberAmountsFromRecord(record: Record<string, unknown> | undefined): MemberAmounts {
  if (!record) return { ...ZERO }
  return {
    JR: Number(record.JR ?? 0),
    KB: Number(record.KB ?? 0),
    LVW: Number(record.LVW ?? 0),
    TR: Number(record.TR ?? 0),
    SM: Number(record.SM ?? 0),
  }
}

function sumMembers(amounts: MemberAmounts): number {
  return MEMBERS.reduce((sum, member) => sum + amounts[member], 0)
}

import { tierColumnTotal } from '@/utils/waterfallGrid'

function memberRecord(amounts: MemberAmounts): MemberAmounts {
  return { JR: amounts.JR, KB: amounts.KB, LVW: amounts.LVW, TR: amounts.TR, SM: amounts.SM }
}

function subtractMembers(a: MemberAmounts, b: MemberAmounts): MemberAmounts {
  return {
    JR: a.JR - b.JR,
    KB: a.KB - b.KB,
    LVW: a.LVW - b.LVW,
    TR: a.TR - b.TR,
    SM: a.SM - b.SM,
  }
}

function addMembers(a: MemberAmounts, b: MemberAmounts): MemberAmounts {
  return {
    JR: a.JR + b.JR,
    KB: a.KB + b.KB,
    LVW: a.LVW + b.LVW,
    TR: a.TR + b.TR,
    SM: a.SM + b.SM,
  }
}

function hasNonZero(amounts: MemberAmounts, threshold = 0.005): boolean {
  return MEMBERS.some((member) => Math.abs(amounts[member]) > threshold)
}

function round2(value: number): number {
  return Math.round(value * 100) / 100
}

function capAdjustmentRow(
  components: MemberAmounts[],
  actual: MemberAmounts,
): MemberAmounts | null {
  const summed = components.reduce((acc, row) => addMembers(acc, row), { ...ZERO })
  const delta = subtractMembers(actual, summed)
  if (!hasNonZero(delta)) return null
  return memberRecord({
    JR: round2(delta.JR),
    KB: round2(delta.KB),
    LVW: round2(delta.LVW),
    TR: round2(delta.TR),
    SM: round2(delta.SM),
  })
}

function cashInRow(step: Record<string, unknown>, label: string): BuildupRow | null {
  const entering = Number(step.cash_available_entering ?? 0)
  if (entering <= 0.005) return null
  return {
    kind: 'cash_in',
    label,
    amounts: { ...ZERO },
    total: entering,
    indent: 1,
  }
}

function tier1Buildup(step: Record<string, unknown>, tier: MemberAmounts): BuildupRow[] {
  const rows: BuildupRow[] = []
  const cashIn = cashInRow(step, 'Cash available entering tier')
  if (cashIn) rows.push(cashIn)

  const trueUp = memberAmountsFromRecord(step.true_up_paid_now as Record<string, unknown>)
  const current = memberAmountsFromRecord(step.stub_accrual as Record<string, unknown>)
  if (hasNonZero(trueUp)) {
    rows.push({
      kind: 'true_up',
      label: 'Accrual adjustment (true-up)',
      amounts: trueUp,
      total: sumMembers(trueUp),
      indent: 1,
    })
  }
  if (hasNonZero(current)) {
    rows.push({
      kind: 'current_period',
      label: 'Current-period allocation',
      amounts: current,
      total: sumMembers(current),
      indent: 1,
    })
  }

  const cap = capAdjustmentRow([trueUp, current], tier)
  if (cap) {
    rows.push({
      kind: 'cap_adjustment',
      label: 'Cash cap adjustment',
      amounts: cap,
      total: sumMembers(cap),
      indent: 1,
    })
  }

  rows.push({
    kind: 'subtotal',
    label: 'Tier subtotal',
    amounts: tier,
    total: tierColumnTotal(tier),
    indent: 0,
  })

  const remaining = Number(step.remaining_after ?? 0)
  rows.push({
    kind: 'remaining',
    label: 'Remaining for next tier',
    amounts: { ...ZERO },
    total: remaining,
    indent: 0,
  })
  return rows
}

function tier2Buildup(step: Record<string, unknown>, tier: MemberAmounts): BuildupRow[] {
  const rows: BuildupRow[] = []

  const trueUp = memberAmountsFromRecord(step.true_up_paid_now as Record<string, unknown>)
  const matchBackpaid = memberAmountsFromRecord(
    step.match_on_backpaid_t1 as Record<string, unknown> | undefined,
  )
  const matchCurrent = memberAmountsFromRecord(
    step.match_on_current_t1 as Record<string, unknown> | undefined,
  )
  const legacyCurrent = memberAmountsFromRecord(
    step.new_repayment_2x_this_t1 as Record<string, unknown>,
  )
  const hasDecomposedMatch =
    hasNonZero(matchBackpaid) || hasNonZero(matchCurrent)

  if (hasNonZero(trueUp)) {
    rows.push({
      kind: 'true_up',
      label: 'Prior HP shortfall (true-up)',
      amounts: trueUp,
      total: sumMembers(trueUp),
      indent: 1,
    })
  }
  if (hasDecomposedMatch) {
    if (hasNonZero(matchBackpaid)) {
      rows.push({
        kind: 'true_up',
        label: '2× match on back-paid Tier 1',
        amounts: matchBackpaid,
        total: sumMembers(matchBackpaid),
        indent: 1,
      })
    }
    if (hasNonZero(matchCurrent)) {
      rows.push({
        kind: 'current_period',
        label: '2× match on current Tier 1',
        amounts: matchCurrent,
        total: sumMembers(matchCurrent),
        indent: 1,
      })
    }
  } else if (hasNonZero(legacyCurrent)) {
    rows.push({
      kind: 'current_period',
      label: 'Current-period allocation (2× this Tier 1)',
      amounts: legacyCurrent,
      total: sumMembers(legacyCurrent),
      indent: 1,
    })
  }

  const currentComponents = hasDecomposedMatch
    ? [trueUp, matchBackpaid, matchCurrent]
    : [trueUp, legacyCurrent]
  const cap = capAdjustmentRow(currentComponents, tier)
  if (cap) {
    rows.push({
      kind: 'cap_adjustment',
      label: 'Cash cap adjustment',
      amounts: cap,
      total: sumMembers(cap),
      indent: 1,
    })
  }

  rows.push({
    kind: 'subtotal',
    label: 'Tier subtotal',
    amounts: tier,
    total: tierColumnTotal(tier),
    indent: 0,
  })

  rows.push({
    kind: 'remaining',
    label: 'Remaining for next tier',
    amounts: { ...ZERO },
    total: Number(step.remaining_after ?? 0),
    indent: 0,
  })
  return rows
}

function tier3Buildup(
  step: Record<string, unknown>,
  tierKey: 's3a' | 's3b',
  tier: MemberAmounts,
): BuildupRow[] {
  const rows: BuildupRow[] = []
  const isA = tierKey === 's3a'
  const classLabel = isA ? 'Class A' : 'Class B'

  if (isA) {
    const cashIn = cashInRow(step, 'Cash available entering tier')
    if (cashIn) rows.push(cashIn)
  }

  const trueUp = memberAmountsFromRecord(
    (isA ? step.classA_true_up : step.classB_true_up) as Record<string, unknown>,
  )
  const current = memberAmountsFromRecord(
    (isA ? step.classA_flow : step.classB_flow) as Record<string, unknown>,
  )

  if (hasNonZero(trueUp)) {
    rows.push({
      kind: 'true_up',
      label: `${classLabel} accrual adjustment`,
      amounts: trueUp,
      total: sumMembers(trueUp),
      indent: 1,
    })
  }
  if (hasNonZero(current)) {
    rows.push({
      kind: 'current_period',
      label: `${classLabel} current-period allocation`,
      amounts: current,
      total: sumMembers(current),
      indent: 1,
    })
  }

  const cap = capAdjustmentRow([trueUp, current], tier)
  if (cap) {
    rows.push({
      kind: 'cap_adjustment',
      label: `${classLabel} cash cap adjustment`,
      amounts: cap,
      total: sumMembers(cap),
      indent: 1,
    })
  }

  rows.push({
    kind: 'subtotal',
    label: `${classLabel} subtotal`,
    amounts: tier,
    total: tierColumnTotal(tier),
    indent: 0,
  })

  if (tierKey === 's3b') {
    rows.push({
      kind: 'remaining',
      label: 'Remaining for next tier',
      amounts: { ...ZERO },
      total: Number(step.remaining_after ?? 0),
      indent: 0,
    })
  }
  return rows
}

function tier4Buildup(
  step: Record<string, unknown>,
  tierKey: 's4a' | 's4b',
  tier: MemberAmounts,
): BuildupRow[] {
  const rows: BuildupRow[] = []
  const isA = tierKey === 's4a'

  if (isA) {
    const cashIn = cashInRow(step, 'Cash available entering tier')
    if (cashIn) rows.push(cashIn)
  }

  const payKey = isA ? 'pay_now_A' : 'pay_now_B'
  const current = memberAmountsFromRecord(step[payKey] as Record<string, unknown>)
  if (hasNonZero(current)) {
    rows.push({
      kind: 'current_period',
      label: isA ? 'Class A pro-rata allocation' : 'Class B pro-rata allocation',
      amounts: current,
      total: sumMembers(current),
      indent: 1,
    })
  }

  rows.push({
    kind: 'subtotal',
    label: isA ? 'Class A subtotal' : 'Class B subtotal',
    amounts: tier,
    total: tierColumnTotal(tier),
    indent: 0,
  })

  if (tierKey === 's4b') {
    rows.push({
      kind: 'remaining',
      label: 'Undistributed remainder',
      amounts: { ...ZERO },
      total: Number(step.remaining_after ?? 0),
      indent: 0,
    })
  }
  return rows
}

export function buildupRowsForTier(
  tierKey: keyof TierBreakdown,
  result: ProposalResponse,
  proposalGrid: TierBreakdown,
): BuildupRow[] {
  const tier = proposalGrid[tierKey]
  const steps = result.steps as unknown as Record<string, unknown>[]

  if (tierKey === 't1' && steps[0]) {
    return tier1Buildup(steps[0], tier)
  }
  if (tierKey === 'hp' && steps[1]) {
    return tier2Buildup(steps[1], tier)
  }
  if ((tierKey === 's3a' || tierKey === 's3b') && steps[2]) {
    return tier3Buildup(steps[2], tierKey, tier)
  }
  if ((tierKey === 's4a' || tierKey === 's4b') && steps[3]) {
    return tier4Buildup(steps[3], tierKey, tier)
  }
  return []
}

export function buildupDetailRowsForTier(
  tierKey: keyof TierBreakdown,
  result: ProposalResponse,
  proposalGrid: TierBreakdown,
): BuildupRow[] {
  return buildupRowsForTier(tierKey, result, proposalGrid).filter(
    (row) => row.kind !== 'subtotal' && row.kind !== 'remaining',
  )
}

export function buildupRemainingRowForTier(
  tierKey: keyof TierBreakdown,
  result: ProposalResponse,
  proposalGrid: TierBreakdown,
): BuildupRow | null {
  return (
    buildupRowsForTier(tierKey, result, proposalGrid).find((row) => row.kind === 'remaining') ??
    null
  )
}

export function tierHasTrueUpBuildup(
  tierKey: keyof TierBreakdown,
  result: ProposalResponse,
  proposalGrid: TierBreakdown,
): boolean {
  return buildupRowsForTier(tierKey, result, proposalGrid).some((row) => row.kind === 'true_up')
}

export function tierHasBuildup(tierKey: keyof TierBreakdown, proposalGrid: TierBreakdown): boolean {
  const total = tierColumnTotal(proposalGrid[tierKey])
  if (total === 0 && tierKey.startsWith('s4')) return false
  return true
}

export function formatBuildupCell(
  kind: BuildupRowKind,
  member: MemberCode,
  row: BuildupRow,
): string | null {
  if (kind === 'cash_in' || kind === 'remaining') {
    return member === 'JR' ? String(row.total) : null
  }
  const value = row.amounts[member]
  if (Math.abs(value) <= 0.005) return '—'
  return String(value)
}
